import { useEffect } from 'react';
import { SITE_NAME, SITE_URL, SITE_FULL_NAME, SITE_DESCRIPTION, SITE_OG_IMAGE, SITE_KEYWORDS } from '@/core/config/content';

export interface SeoOptions {
  title?: string | null;
  description?: string;
  /** Path only, e.g. `/faq`. */
  canonical?: string;
  keywords?: string | string[];
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  noindex?: boolean;
  /** schema.org payload (Article, FAQPage…) for search / answer engines. */
  jsonLd?: Record<string, unknown>;
}

const JSON_LD_ID = 'dynamic-jsonld';

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Per-page <title>, description, canonical, Open Graph, Twitter Cards and JSON-LD for Search Engines, GEO & AI crawlers. */
export function useSEO({
  title,
  description = SITE_DESCRIPTION,
  canonical,
  keywords = SITE_KEYWORDS,
  ogImage = SITE_OG_IMAGE,
  ogType = 'website',
  noindex = false,
  jsonLd,
}: SeoOptions = {}) {
  const ld = jsonLd ? JSON.stringify(jsonLd) : null;
  const kw = Array.isArray(keywords) ? keywords.join(', ') : keywords;

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | ${SITE_FULL_NAME}`;
    document.title = fullTitle;

    // Standard meta
    setMeta('meta[name="description"]', 'name', 'description', description);
    if (kw) setMeta('meta[name="keywords"]', 'name', 'keywords', kw);
    setMeta('meta[name="robots"]', 'name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // Open Graph
    const pageUrl = `${SITE_URL}${canonical ?? '/'}`;
    setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', pageUrl);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_FULL_NAME);
    setMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMeta('meta[property="og:locale"]', 'property', 'og:locale', 'fa_IR');
    setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);

    // Twitter Cards
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    // Canonical link
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = pageUrl;

    // JSON-LD structured data
    document.getElementById(JSON_LD_ID)?.remove();
    if (ld) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSON_LD_ID;
      script.textContent = ld;
      document.head.appendChild(script);
    }
    return () => document.getElementById(JSON_LD_ID)?.remove();
  }, [title, description, canonical, kw, ogImage, ogType, noindex, ld]);
}
